import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SymptomChecker from '../components/SymptomChecker';
import EmergencyAlert from '../components/EmergencyAlert';
import AmbulanceTracker from '../components/AmbulanceTracker';
import {
  Warning as EmergencyIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  Favorite as HeartIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Psychology as AIIcon,
  Medication as MedicationIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  LocalHospital as CaregiverIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface PatientInfo {
  id: string;
  name: string;
  age: number;
  photo?: string;
  status: 'stable' | 'monitoring' | 'emergency';
  lastUpdate: string;
  medications: Array<{ name: string; schedule: string }>;
  nextAppointment?: { date: string; doctor: string; type: string };
  vitalSigns: {
    heartRate: number;
    bloodPressure: string;
    glucose: number;
    temperature: number;
  };
}

interface FamilyMemberDashboardProps {
  memberRole: 'viewer' | 'caregiver' | 'emergency_contact';
  patientId?: string;
}

const FamilyMemberDashboard: React.FC<FamilyMemberDashboardProps> = ({ 
  memberRole = 'caregiver',
  patientId = '1' 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [patient, setPatient] = useState<PatientInfo>({
    id: patientId,
    name: 'Juan Pérez',
    age: 68,
    status: 'stable',
    lastUpdate: '15 minutos',
    medications: [
      { name: 'Aspirina 100mg', schedule: 'Mañana' },
      { name: 'Enalapril 10mg', schedule: 'Mañana y noche' },
      { name: 'Metformina 850mg', schedule: 'Con comidas' }
    ],
    nextAppointment: {
      date: '15 de Julio, 10:00',
      doctor: 'Dr. Roberto Silva',
      type: 'Control Cardiología'
    },
    vitalSigns: {
      heartRate: 72,
      bloodPressure: '120/80',
      glucose: 98,
      temperature: 36.5
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'stable': return 'success';
      case 'monitoring': return 'warning';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'stable': return <CheckIcon />;
      case 'monitoring': return <InfoIcon />;
      case 'emergency': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'stable': return 'Estable';
      case 'monitoring': return 'En Observación';
      case 'emergency': return 'Emergencia';
      default: return status;
    }
  };

  const getRoleBadge = () => {
    switch (memberRole) {
      case 'caregiver':
        return <Chip icon={<CaregiverIcon />} label="Cuidador" color="primary" size="small" />;
      case 'emergency_contact':
        return <Chip icon={<EmergencyIcon />} label="Contacto de Emergencia" color="error" size="small" />;
      default:
        return <Chip icon={<ViewIcon />} label="Observador" size="small" />;
    }
  };

  const handleEmergency = () => {
    if (window.confirm(`¿Activar alerta de emergencia para ${patient.name}?`)) {
      setEmergencyOpen(true);
      // In real app: trigger emergency protocol
    }
  };

  const handleCall = () => {
    window.open('tel:+56912345678');
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/56912345678?text=Hola ${patient.name}, soy tu familiar del círculo de cuidado.`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isSmallMobile ? 2 : 4, mb: 4 }}>
      {/* Header with patient info */}
      <Paper sx={{ 
        p: isSmallMobile ? 2 : 3, 
        mb: 3, 
        bgcolor: 'primary.main', 
        color: 'white' 
      }}>
        <Grid container alignItems="center" spacing={isSmallMobile ? 1 : 2}>
          <Grid item xs={12} sm="auto" sx={{ textAlign: isSmallMobile ? 'center' : 'left' }}>
            <Avatar 
              sx={{ 
                width: isSmallMobile ? 100 : 80, 
                height: isSmallMobile ? 100 : 80, 
                bgcolor: 'white', 
                color: 'primary.main',
                mx: isSmallMobile ? 'auto' : 0,
                fontSize: isSmallMobile ? '2rem' : '1.5rem'
              }}
            >
              {patient.name.split(' ').map(n => n[0]).join('')}
            </Avatar>
          </Grid>
          <Grid item xs={12} sm>
            <Box sx={{ textAlign: isSmallMobile ? 'center' : 'left' }}>
              <Typography 
                variant={isSmallMobile ? "h3" : "h4"} 
                gutterBottom
                sx={{ 
                  fontSize: isSmallMobile ? '2.5rem' : undefined,
                  fontWeight: 'bold'
                }}
              >
                {patient.name}
              </Typography>
              <Typography 
                variant={isSmallMobile ? "h6" : "body1"}
                sx={{ 
                  fontSize: isSmallMobile ? '1.25rem' : undefined,
                  fontWeight: isSmallMobile ? 'medium' : 'normal'
                }}
              >
                {patient.age} años • Última actualización: hace {patient.lastUpdate}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isSmallMobile ? 'row' : 'column', 
              gap: 1, 
              alignItems: isSmallMobile ? 'center' : 'flex-end',
              justifyContent: isSmallMobile ? 'center' : 'flex-end'
            }}>
              {getRoleBadge()}
              <Chip 
                icon={getStatusIcon(patient.status)}
                label={getStatusText(patient.status)}
                color={getStatusColor(patient.status) as any}
                size={isSmallMobile ? 'medium' : 'small'}
                sx={{ 
                  bgcolor: 'white',
                  fontWeight: 'bold',
                  fontSize: isSmallMobile ? '1rem' : undefined,
                  '& .MuiChip-icon': { color: `${getStatusColor(patient.status)}.main` }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Quick Actions - Always visible */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: patient.status === 'emergency' ? '#ffebee' : undefined }}>
            <CardContent sx={{ p: isSmallMobile ? 4 : 3 }}>
              <Typography 
                variant={isSmallMobile ? "h4" : "h6"} 
                gutterBottom
                sx={{ 
                  textAlign: isSmallMobile ? 'center' : 'left',
                  fontWeight: 'bold',
                  mb: isSmallMobile ? 3 : 2
                }}
              >
                Acciones Rápidas
              </Typography>
              <Grid container spacing={isSmallMobile ? 4 : 2}>
                {/* Emergency Button - Always available */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<EmergencyIcon fontSize={isSmallMobile ? "large" : "medium"} />}
                    onClick={handleEmergency}
                    sx={{ 
                      py: isSmallMobile ? 4 : 2,
                      fontSize: isSmallMobile ? '1.5rem' : '1.1rem',
                      fontWeight: 'bold',
                      minHeight: isSmallMobile ? '80px' : '56px',
                      borderRadius: 2,
                      boxShadow: 3,
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                      '&:hover': {
                        boxShadow: 6,
                      }
                    }}
                  >
                    EMERGENCIA
                  </Button>
                </Grid>
                
                {/* Call Button */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<PhoneIcon fontSize={isSmallMobile ? "large" : "medium"} />}
                    onClick={handleCall}
                    sx={{ 
                      py: isSmallMobile ? 4 : 2,
                      fontSize: isSmallMobile ? '1.5rem' : '1.1rem',
                      fontWeight: 'bold',
                      minHeight: isSmallMobile ? '80px' : '56px',
                      borderRadius: 2,
                      '&:active': {
                        transform: 'scale(0.98)',
                      }
                    }}
                  >
                    Llamar
                  </Button>
                </Grid>
                
                {/* WhatsApp Button */}
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    color="success"
                    startIcon={<WhatsAppIcon fontSize={isSmallMobile ? "large" : "medium"} />}
                    onClick={handleWhatsApp}
                    sx={{ 
                      py: isSmallMobile ? 4 : 2,
                      fontSize: isSmallMobile ? '1.5rem' : '1.1rem',
                      fontWeight: 'bold',
                      minHeight: isSmallMobile ? '80px' : '56px',
                      borderRadius: 2,
                      '&:active': {
                        transform: 'scale(0.98)',
                      }
                    }}
                  >
                    WhatsApp
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Ambulance Tracker - Always visible during emergencies */}
        <Grid item xs={12}>
          <AmbulanceTracker 
            emergencyId="EMG001"
            patientName={patient.name}
            isActive={true} // In real app: check if emergency is active
          />
        </Grid>

        {/* Emergency Contacts - Always visible */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Contactos de Emergencia
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Dr. Roberto Silva" 
                  secondary="Cardiólogo - +56 9 1111 2222"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmergencyIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Ambulancia" 
                  secondary="131"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Symptom Checker - Only for caregivers */}
        {memberRole === 'caregiver' && (
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <AIIcon sx={{ mr: 1 }} />
                  Reportar Síntomas de {patient.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Como cuidador, puedes reportar síntomas en nombre de {patient.name}
                </Typography>
                <SymptomChecker isProxySubmission={true} patientName={patient.name} />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Medications - Visible to caregivers and viewers */}
        {(memberRole === 'caregiver' || memberRole === 'viewer') && (
          <Grid item xs={12} md={memberRole === 'caregiver' ? 8 : 12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                <MedicationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Medicamentos
              </Typography>
              <List>
                {patient.medications.map((med, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={med.name}
                      secondary={med.schedule}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Next Appointment - Visible to all family members */}
        {patient.nextAppointment && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<AssignmentIcon />}>
              <Typography variant="subtitle2">
                <strong>Próxima cita:</strong> {patient.nextAppointment.type} - {patient.nextAppointment.date} con {patient.nextAppointment.doctor}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Role-based message */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2" color="text.secondary">
              {memberRole === 'viewer' && 
                `Como observador, puedes ver el estado de salud de ${patient.name} y contactarlo en caso de necesidad.`
              }
              {memberRole === 'caregiver' && 
                `Como cuidador, puedes reportar síntomas y gestionar la salud de ${patient.name}. Usa el botón de emergencia si es necesario.`
              }
              {memberRole === 'emergency_contact' && 
                `Como contacto de emergencia, recibirás alertas críticas. Usa el botón de emergencia si ${patient.name} necesita ayuda inmediata.`
              }
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Emergency Alert Dialog */}
      <EmergencyAlert
        open={emergencyOpen}
        onClose={() => setEmergencyOpen(false)}
        patientName={patient.name}
        triggeredBy="María González"
        isProxyTrigger={true}
      />
    </Container>
  );
};

export default FamilyMemberDashboard;